// SEQ-193: the By month grid of the Project performance table. A row per
// project, a group of metric columns per month, then a Total group.
//
// Pure presentation. Everything it decides (which cells are blank, the sort)
// comes from projectMonthTable.ts and
// projectMonthView.ts, so what is drawn here is what those modules tested.
//
// The By month view sits in its own scroll container (.pnl-table-scroll--months)
// so a wide grid scrolls inside the card, never the page. The Project column is
// pinned, and each month group has a rule down its left edge.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, RefreshCw } from "lucide-react";
import { formatMonthLabel, formatPnlMetric } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { COSTS_NOT_ENTERED_LABEL } from "../../features/pnlAnalytics/projectMonthCosts";
import {
  projectMonthCell,
  TOTAL_GROUP,
  type ProjectMonthRow,
  type ProjectMonthTable,
} from "../../features/pnlAnalytics/projectMonthTable";

export interface MonthGridMetric {
  key: string;
  label: string;
  unit: PnlUnit;
}

export interface MonthGridMessage {
  text: string;
  alert: boolean;
  retry: boolean;
}

export interface PnlProjectMonthGridProps {
  table: ProjectMonthTable | null;
  /** What stands in for the grid when there is nothing to draw (loading, error, bad range, no match). */
  message: MonthGridMessage | null;
  onRetry: () => void;
  metrics: MonthGridMetric[];
  rows: ProjectMonthRow[];
  sort: { group: string; key: string };
  sortDirection: "asc" | "desc";
  onSort: (group: string, key: string) => void;
  // The Total heading: "Total", or "Total, last 6 months" when a shorter window is chosen.
  totalLabel?: string;
  // SEQ-232: pinned first in the body, never sorted. Null when there is nothing to total.
  allProjectsRow?: ProjectMonthRow | null;
  /**
   * SEQ-240: the "How is this worked out" trigger for one metric column. Drawn
   * once per column, in the Total group's heading, so a grid of many months
   * carries one explanation per figure rather than one per month.
   */
  columnInfo?: (metric: MonthGridMetric) => ReactNode;
  /** SEQ-240: the trigger for the Project column heading. */
  projectInfo?: ReactNode;
}

const headStyle = {
  padding: "8px 10px", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)",
  whiteSpace: "nowrap" as const,
};

function formatValue(value: number | null, metric: MonthGridMetric): string {
  if (value === null) return ""; // an empty month is blank, never $0
  if (metric.unit === "percent" || metric.unit === "currency") return formatPnlMetric(value, metric.unit);
  return formatMetricValue(value, metric.unit);
}

export function PnlProjectMonthGrid({ table, message, onRetry, metrics, rows, sort, sortDirection, onSort, totalLabel = "Total", allProjectsRow = null, columnInfo, projectInfo }: PnlProjectMonthGridProps) {
  if (message || !table) {
    return (
      <div
        role={message?.alert ? "alert" : "status"}
        style={{ flex: 1, minHeight: 200, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}
      >
        <p style={{ margin: 0 }}>{message?.text}</p>
        {message?.retry && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
            <RefreshCw size={13} aria-hidden="true" /> Retry
          </button>
        )}
      </div>
    );
  }

  const groups = [
    ...table.columns.map((c) => ({ group: c.periodMonth, label: formatMonthLabel(c.periodMonth), partial: c.partial })),
    { group: TOTAL_GROUP, label: totalLabel, partial: false },
  ];

  // One row's cells. A month the switch has left out reads as a single "No costs entered" cell across the
  // month's columns (text, never colour alone); a month shown anyway keeps its figures with the same words
  // under the first one.
  const renderCells = (row: ProjectMonthRow) =>
    groups.flatMap((g) => {
      const isMonth = g.group !== TOTAL_GROUP;
      if (isMonth && row.excluded?.has(g.group)) {
        return [
          <td
            key={`${g.group}-nocosts`}
            colSpan={metrics.length}
            className="pnl-month-group-start pnl-month-nocost"
            style={{ padding: "8px 10px", textAlign: "center", whiteSpace: "nowrap", verticalAlign: "top" }}
          >
            {COSTS_NOT_ENTERED_LABEL}
          </td>,
        ];
      }
      const unfinished = isMonth && row.noCosts?.has(g.group);
      return metrics.map((metric, index) => {
        const value = projectMonthCell(row, g.group, metric.key);
        const isNegativeMoney =
          (metric.unit === "currency" || metric.unit === "currency_per_hour") && typeof value === "number" && value < 0;
        return (
          <td
            key={`${g.group}-${metric.key}`}
            data-seq-private=""
            className={[index === 0 ? "pnl-month-group-start" : "", isNegativeMoney ? "pnl-table-negative" : "", g.group === TOTAL_GROUP ? "pnl-month-total-cell" : ""].filter(Boolean).join(" ") || undefined}
            style={{ padding: "8px 10px", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top" }}
          >
            {formatValue(value, metric)}
            {unfinished && index === 0 && <span className="pnl-month-nocost pnl-month-nocost--inline">{COSTS_NOT_ENTERED_LABEL}</span>}
          </td>
        );
      });
    });

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>
          <th
            rowSpan={2}
            scope="col"
            className="pnl-sticky-col"
            style={{ ...headStyle, textAlign: "left", verticalAlign: "bottom", borderBottom: "2px solid var(--border-color)" }}
          >
            <span className="hwo-heading">
              Project
              {projectInfo}
            </span>
          </th>
          {groups.map((g) => (
            <th
              key={g.group}
              colSpan={metrics.length}
              scope="colgroup"
              className="pnl-month-group-start"
              style={{ ...headStyle, textAlign: "center", borderBottom: "1px solid var(--border-color)" }}
            >
              {g.label}
              {g.partial && (
                <span style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>Month to date</span>
              )}
            </th>
          ))}
        </tr>
        <tr>
          {groups.flatMap((g) =>
            metrics.map((metric, index) => {
              const isSorted = sort.group === g.group && sort.key === metric.key;
              const ariaSort = isSorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none";
              return (
                <th
                  key={`${g.group}-${metric.key}`}
                  scope="col"
                  aria-sort={ariaSort}
                  className={index === 0 ? "pnl-month-group-start" : undefined}
                  style={{ ...headStyle, textAlign: "right", padding: "6px 10px", borderBottom: "2px solid var(--border-color)" }}
                >
                  <span className="hwo-heading">
                    <button
                      type="button"
                      className="pnl-table-sort"
                      onClick={() => onSort(g.group, metric.key)}
                      style={{ flexDirection: "row-reverse" }}
                    >
                      {metric.label}
                      {isSorted ? (
                        sortDirection === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                      ) : (
                        <ArrowUpDown size={12} style={{ opacity: 0.4 }} />
                      )}
                    </button>
                    {g.group === TOTAL_GROUP && columnInfo?.(metric)}
                  </span>
                </th>
              );
            }),
          )}
        </tr>
      </thead>
      <tbody>
        {allProjectsRow && (
          <tr key="all-projects" className="pnl-month-summary-row">
            <th scope="row" className="pnl-sticky-col pnl-month-project-cell" style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, verticalAlign: "top" }}>
              {allProjectsRow.projectName}
            </th>
            {renderCells(allProjectsRow)}
          </tr>
        )}
        {rows.map((row) => (
          <tr key={row.projectId} style={{ borderBottom: "1px solid var(--border-color-light)" }} className="pnl-table-row">
            <th scope="row" className="pnl-sticky-col pnl-month-project-cell" style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, verticalAlign: "top" }}>
              <Link className="pnl-table-link pnl-month-project-link" to={`/projects/${row.projectId}?tab=status`} title={row.projectName}>
                {row.projectName}
              </Link>
            </th>
            {renderCells(row)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
